// Static seed data mirrored from backend seed — used for first-paint SEO content
// so pages return meaningful HTML even before client-side fetch resolves.

export const COMPANY = {
  name: "Salihi Group",
  tagline: "Integrated Excellence. Trusted Growth.",
  established: 2020,
  address: "1st Floor, Blue Building, Opp ABC Centre, Aden Abdulle International Airport, Mogadishu, Somalia",
  phone: "+252 615 243 670",
  whatsapp: "+252615243670",
  email: "info@salihigroup.com",
  website: "www.salihigroup.com",
  hours: "Saturday – Thursday, 8:00 AM – 5:00 PM",
  mapLat: 2.0130629,
  mapLng: 45.2996655,
  shortDescription: "Salihi Group is a diversified professional services group established in 2020 and headquartered in Mogadishu, Somalia. Its seven specialised companies bring legal, consulting, engineering, investment, mining, Oil & Gas, and workforce services together under one trusted corporate platform.",
  purpose: "The Group exists to give clients access to the full range of professional services they need through a single, coordinated relationship — with consistent standards, shared governance, and one point of accountability.",
  vision: "To be a respected, integrated professional services group in Somalia — recognised for the quality of its work, the integrity of its people, and its long-term contribution to sustainable growth.",
  mission: "To provide clients with dependable, professionally delivered legal, consulting, engineering, investment, mining, Oil & Gas, and workforce services — coordinated across seven specialised companies operating under one corporate platform.",
};

export const SUBSIDIARIES = [
  {
    slug: "salihi-law",
    name: "Salihi Law & Legal Services",
    tagline: "Legal Advisory",
    description: "Corporate legal advisory, contract work, and regulatory support delivered with discretion and clear documentation.",
    icon: "Scale",
    logo_kind: "real",
    logo_path: "/api/media/branding/subsidiaries/salihi-law.png",
    overview: [
      "Salihi Law & Legal Services is the legal practice of Salihi Group. The firm advises private companies, investors, and public institutions on the legal issues that arise across their commercial activities in Somalia.",
      "The practice combines local legal insight with an internationally informed approach. Every engagement is handled under the Group's confidentiality and quality standards — with clear scope, documented advice, and senior oversight.",
      "As part of Salihi Group, the firm can coordinate seamlessly with our consulting, investment, engineering, mining, and workforce companies whenever a client's needs cross disciplines.",
    ],
    services: [
      "Corporate and commercial legal advisory",
      "Contract drafting, review, and negotiation",
      "Regulatory and compliance support",
      "Employment and labour matters",
      "Legal research and opinions",
      "Dispute prevention and resolution support",
      "Government and institutional liaison",
      "Business establishment and licensing support",
    ],
    sectors: ["Private companies", "Investors", "Public institutions", "Development organisations"],
    strengths: [
      "Confidentiality-first engagement model",
      "Bilingual documentation and correspondence",
      "Coordination with Salihi Consulting on compliance and licensing matters",
    ],
  },
  {
    slug: "salihi-consulting",
    name: "Salihi Consulting",
    tagline: "Business Advisory",
    description: "Business advisory, institutional consulting, and administrative support for organisations building or scaling in Somalia.",
    icon: "Briefcase",
    logo_kind: "real",
    logo_path: "/api/media/branding/subsidiaries/salihi-consulting.png",
    overview: [
      "Salihi Consulting is the business advisory arm of Salihi Group. We work with corporate clients, public institutions, development organisations, and entrepreneurs on the practical questions that drive growth or hold it back.",
      "Our role is to bring order, documentation, and disciplined follow-through to work that is often complex and context-dependent. We deliver clear, structured outputs that leadership teams can act on.",
      "Where an engagement requires legal input, engineering advice, or workforce support, we coordinate with the relevant Salihi Group companies so clients receive a single, coherent response.",
    ],
    services: [
      "Business advisory",
      "Institutional consulting",
      "Compliance support",
      "Tax and accounting assistance",
      "Documentation and administrative support",
      "Immigration facilitation",
      "Project administration",
      "Management systems",
      "Digital and software solutions",
      "Capacity building",
    ],
    sectors: ["Corporate clients", "Public institutions", "Development organisations", "Entrepreneurs and business owners"],
    strengths: [
      "Solution-oriented approach",
      "Bilingual project delivery",
      "Discreet stakeholder engagement",
    ],
  },
  {
    slug: "salihi-mining",
    name: "Salihi Mining",
    tagline: "Natural Resources",
    description: "Natural resources advisory, licensing coordination, and responsible mining support across Somalia's minerals sector.",
    icon: "Mountain",
    logo_kind: "real",
    logo_path: "/api/media/branding/subsidiaries/salihi-mining.png",
    overview: [
      "Salihi Mining supports operators, investors, and public bodies working in Somalia's natural resources sector. Our role is to bring rigour and structure to activities where local knowledge, regulatory navigation, and community engagement determine outcomes.",
      "We work discreetly with clients on exploration coordination, licensing, community liaison, and responsible-practice frameworks — always within the Group's confidentiality and governance standards.",
      "For legal structuring, investment coordination, or environmental engineering support, Salihi Mining draws on the wider Salihi Group platform.",
    ],
    services: [
      "Mining and natural resources advisory",
      "Regulatory coordination",
      "Licensing support",
      "Stakeholder engagement",
      "Investment facilitation",
      "Responsible mining practices",
      "Environmental and social compliance",
      "Sector research and project support",
    ],
    sectors: ["Investors", "Mining and natural resources operators", "Public institutions"],
    strengths: [
      "On-the-ground coordination capacity",
      "Alignment across regulatory and community stakeholders",
      "Responsible-practice framework applied to every engagement",
    ],
  },
  {
    slug: "salihi-engineering",
    name: "Salihi Engineering Consulting",
    tagline: "Engineering Advisory",
    description: "Engineering advisory, project coordination, and technical documentation for construction and infrastructure projects.",
    icon: "HardHat",
    logo_kind: "real",
    logo_path: "/api/media/branding/subsidiaries/salihi-engineering.png",
    overview: [
      "Salihi Engineering Consulting supports construction and infrastructure projects with engineering advisory, coordination, and technical documentation. Our involvement typically ranges from early planning through independent technical review.",
      "We bring structure to the moving parts of a project — design coordination, documentation, quality controls, and disciplined stakeholder communication — so that leadership teams and financiers can rely on a clear technical picture.",
      "Where a project touches legal, investment, workforce, or environmental issues, we draw on the relevant Salihi Group companies to deliver a coordinated response.",
    ],
    services: [
      "Engineering advisory",
      "Construction and infrastructure support",
      "Project planning",
      "Technical documentation",
      "Design coordination",
      "Project management",
      "Quality control",
      "Infrastructure development",
      "Environmental and technical compliance",
    ],
    sectors: ["Infrastructure and engineering projects", "Public institutions", "Corporate clients"],
    strengths: [
      "Structured project documentation",
      "Independent technical review",
      "Multi-disciplinary coordination across the Group",
    ],
  },
  {
    slug: "salihi-investment",
    name: "Salihi Investment",
    tagline: "Investment Structuring",
    description: "Investment planning, structuring, and partnership support for investors and business owners active in Somalia.",
    icon: "TrendingUp",
    logo_kind: "real",
    logo_path: "/api/media/branding/subsidiaries/salihi-investment.png",
    overview: [
      "Salihi Investment works with investors and business owners on the structuring, entry, and partnership questions that determine whether an opportunity becomes a business.",
      "Our role is to bring diligence, structured analysis, and local awareness to investment decisions — and to coordinate the legal, regulatory, and operational work that follows.",
      "As part of Salihi Group, we can bring in specialised legal, consulting, engineering, mining, or workforce expertise wherever an investment demands it.",
    ],
    services: [
      "Investment planning",
      "Opportunity assessment",
      "Business structuring",
      "Partnership development",
      "Market entry support",
      "Due diligence coordination",
      "Investment facilitation",
      "Sustainable business growth",
    ],
    sectors: ["Investors", "Private companies", "Entrepreneurs and business owners"],
    strengths: [
      "Discreet deal handling",
      "Local partner network",
      "Structured investment documentation",
    ],
  },
  {
    slug: "kaafi-hire",
    name: "Kaafi Hire Employment Services",
    tagline: "Workforce Solutions",
    description: "Recruitment, staffing, and workforce solutions for employers building dependable teams in Somalia.",
    icon: "Users",
    logo_kind: "real",
    logo_path: "/api/media/branding/subsidiaries/kaafi-hire.png",
    overview: [
      "Kaafi Hire Employment Services is the workforce arm of Salihi Group. We support employers who need dependable, well-matched people — whether for permanent roles, contract assignments, or project mobilisation.",
      "Our screening, documentation, and mobilisation processes are designed to reduce hiring risk and give clients confidence in the people they bring on. Confidentiality of candidate information is maintained throughout.",
      "For clients also requiring legal support, payroll structuring, or workforce policy work, Kaafi Hire coordinates with Salihi Law and Salihi Consulting.",
    ],
    services: [
      "Recruitment",
      "Staffing",
      "Workforce outsourcing",
      "Candidate screening",
      "Employment documentation",
      "Payroll coordination",
      "Workforce mobilisation",
      "Staff deployment",
      "Human resources support",
      "Performance and workforce administration",
    ],
    sectors: ["Employers and HR departments", "Corporate clients", "Public institutions"],
    strengths: [
      "Structured screening pipeline",
      "Local reach with regional visibility",
      "Confidential candidate handling",
    ],
  },
  {
    slug: "salihi-petroleum",
    name: "Salihi Oil & Gas",
    tagline: "Trusted advisory across oil, gas, and energy sectors.",
    description: "Professional oil and gas advisory across the petroleum and energy sector.",
    icon: "Fuel",
    logo_kind: "real",
    logo_path: "/api/media/branding/subsidiaries/salihi-petroleum.png",
    seo_title: "Salihi Oil & Gas | Oil and Gas Advisory in Somalia",
    seo_description: "Salihi Oil & Gas provides professional oil, gas, and energy sector advisory under Salihi Group — supporting clients with petroleum guidance, energy project advisory, investment advisory, and regulatory coordination.",
    overview: [
      "Salihi Oil & Gas is a professional oil and gas advisory services company under Salihi Group, delivering trusted advisory support across the oil, gas, petroleum, and energy sectors.",
      "The company supports clients with petroleum sector guidance, energy project advisory, investment advisory, regulatory and compliance coordination, stakeholder liaison, and strategic sector solutions.",
      "Guided by the standards of Salihi Group, Salihi Oil & Gas is committed to professionalism, reliability, integrity, and long-term client partnership.",
    ],
    services: [
      "Petroleum sector guidance — sector insights and strategic advisory for oil, gas, and energy stakeholders",
      "Energy project advisory — project-level advisory across development, planning, and execution stages",
      "Investment advisory — guidance for investors evaluating oil, gas, and petroleum opportunities",
      "Regulatory and compliance coordination — support in navigating sector regulations and compliance requirements",
      "Stakeholder liaison — coordination between clients, institutions, partners, and public entities",
      "Strategic sector solutions — tailored advisory for long-term positioning in the energy sector",
    ],
    sectors: ["Businesses", "Institutions", "Investors", "Public entities"],
    strengths: [
      "Professional advisory approach",
      "Sector coordination across the wider Salihi Group",
      "Long-term client partnership orientation",
    ],
    brandStatement:
      "Salihi Oil & Gas represents trusted advisory, professional integrity, and long-term partnership across the energy sector.",
  },
];

// 12 sectors — with the type of support Salihi Group provides
export const SECTORS = [
  { name: "Aviation", icon: "Plane",
    description: "Legal, commercial, and workforce support available to airlines, ground handlers, and aviation-linked service providers operating into and out of Somalia." },
  { name: "Banking and Financial Services", icon: "Landmark",
    description: "Advisory, compliance, contract, and consulting support available to banks, financial institutions, and financial service providers." },
  { name: "Professional Services", icon: "Briefcase",
    description: "Legal, consulting, and administrative support offered to firms in law, accounting, audit, and other professional service sectors." },
  { name: "Education", icon: "GraduationCap",
    description: "Consulting, compliance, and workforce support available to schools, training institutions, and international education organisations." },
  { name: "Construction and Infrastructure", icon: "HardHat",
    description: "Engineering advisory, project coordination, technical documentation, and workforce services offered to construction and infrastructure projects." },
  { name: "Mining and Natural Resources", icon: "Mountain",
    description: "Advisory, regulatory coordination, and responsible-practice support available to operators and investors in the natural resources sector." },
  { name: "Investment", icon: "TrendingUp",
    description: "Investment planning, opportunity assessment, structuring, and partnership development for investors exploring the Somalia market." },
  { name: "Employment and Workforce Services", icon: "Users",
    description: "Recruitment, staffing, workforce outsourcing, and HR administration services delivered through Kaafi Hire Employment Services." },
  { name: "Government and Public Institutions", icon: "Building2",
    description: "Institutional consulting, legal advisory, and project administration support available to government bodies and public institutions." },
  { name: "Security", icon: "Shield",
    description: "Legal, consulting, and workforce support offered to security service providers operating under commercial and institutional mandates." },
  { name: "Travel and Hospitality", icon: "Palmtree",
    description: "Advisory, workforce, and administrative support offered to travel operators, hospitality providers, and related service businesses." },
  { name: "International Development Organizations", icon: "Globe",
    description: "Consulting, legal, and workforce support available to international NGOs and development agencies operating programmes in Somalia." },
  { name: "Petroleum & Energy", icon: "Fuel",
    description: "Petroleum supply, trading, fuel distribution support, energy logistics, and dependable corporate energy solutions available through Salihi Oil & Gas." },
];

// 6 approach steps
export const APPROACH = [
  "Understanding the client's objectives",
  "Providing professional advice and practical solutions",
  "Maintaining confidentiality and trust",
  "Delivering clear documentation and structured support",
  "Ensuring quality and consistency across service areas",
  "Supporting long-term client relationships",
];

// 9 pillars
export const WHY_CHOOSE = [
  { title: "Integrated multidisciplinary services",
    body: "Seven specialised companies under one corporate platform. Where an engagement crosses disciplines, we deliver one coordinated response rather than a set of disconnected relationships." },
  { title: "Strong understanding of Somalia's business environment",
    body: "Salihi Group is rooted in Mogadishu. Our people understand how business is actually conducted, coordinated, and resolved in the Somalia market." },
  { title: "Local knowledge with an international outlook",
    body: "We combine local insight with an internationally informed approach to documentation, communication, and professional standards — helping clients bridge both worlds with confidence." },
  { title: "Responsive client support",
    body: "Enquiries are acknowledged promptly and matters are actively managed. Clients know who is handling their file and how it is progressing." },
  { title: "Confidential and ethical service",
    body: "Confidentiality is a default, not an option. Every engagement is handled within a defined ethical framework, with senior oversight." },
  { title: "Practical and commercially focused solutions",
    body: "Advice is delivered so that it can be acted on. We focus on the commercially sensible option, not the theoretically ideal one." },
  { title: "Access to several professional disciplines through one Group",
    body: "Legal, consulting, engineering, investment, mining, Oil & Gas, and workforce expertise are available through a single point of contact." },
  { title: "Long-term relationship approach",
    body: "We build relationships that compound over time. Advice given today should still make sense to the client several years from now." },
  { title: "Clear communication and accountability",
    body: "Scope, deliverables, and status are documented. Ownership of every engagement is clear — internally and to the client." },
];

// 8 core values
export const VALUES = [
  { title: "Integrity", icon: "ShieldCheck", body: "We hold ourselves to honest, principled conduct in every engagement, without exception." },
  { title: "Professional Excellence", icon: "Award", body: "Consistent, quality-controlled delivery across all seven subsidiaries — measured, documented, and reviewed." },
  { title: "Accountability", icon: "ClipboardCheck", body: "Clear ownership of every engagement, senior oversight, and honest reporting of progress and issues." },
  { title: "Confidentiality", icon: "Lock", body: "Client information is treated as confidential by default. Access is controlled and communication is disciplined." },
  { title: "Collaboration", icon: "Users2", body: "Seven specialised teams working as one Group — sharing context, coordinating delivery, avoiding silos." },
  { title: "Innovation", icon: "Lightbulb", body: "Openness to better ways of working — better documentation, better tools, better process — grounded in what actually helps clients." },
  { title: "Local Knowledge", icon: "MapPin", body: "Deep familiarity with the Somalia market — how it works, who matters, and where the risks lie." },
  { title: "Sustainable Growth", icon: "Sprout", body: "We support decisions that build durable value for clients, for the Group, and for the wider economy." },
];

// Governance principles
export const GOVERNANCE_PRINCIPLES = [
  { title: "Corporate oversight", icon: "Building2", body: "Salihi Group provides consolidated oversight of all seven subsidiaries, with shared standards for engagement, delivery, and reporting." },
  { title: "Professional independence", icon: "Scale", body: "Each subsidiary operates with the professional independence its discipline requires, within the Group's governance framework." },
  { title: "Ethical conduct", icon: "ShieldCheck", body: "Ethical standards are set at Group level and applied consistently across every subsidiary and engagement." },
  { title: "Risk management", icon: "AlertTriangle", body: "Engagement risks — legal, commercial, reputational, and operational — are identified, documented, and actively managed." },
  { title: "Compliance", icon: "ClipboardCheck", body: "Regulatory, professional, and internal compliance obligations are treated as non-negotiable." },
  { title: "Confidentiality", icon: "Lock", body: "Access to client information is controlled. Confidentiality is enforced at systems, personnel, and process levels." },
  { title: "Accountability", icon: "UserCheck", body: "Clear ownership of every engagement. Senior partners or directors carry accountability for outcomes." },
  { title: "Quality assurance", icon: "Award", body: "Deliverables are reviewed to Group quality standards before they leave the firm." },
  { title: "Conflict management", icon: "Split", body: "Actual and perceived conflicts of interest are identified early, disclosed, and managed transparently." },
  { title: "Responsible decision-making", icon: "Compass", body: "Decisions are made with an eye to long-term consequences — commercial, ethical, and reputational." },
];

// "How We Can Help" enquiry types on /contact
export const ENQUIRY_TYPES = [
  { title: "Business consultation requests", icon: "Briefcase", body: "For clients seeking advice on business set-up, growth, or day-to-day operations." },
  { title: "Partnership enquiries", icon: "Handshake", body: "For organisations exploring collaboration, joint delivery, or referral partnerships with Salihi Group." },
  { title: "Institutional and investor enquiries", icon: "Landmark", body: "For public institutions, development organisations, and investors evaluating engagement in Somalia." },
  { title: "Recruitment enquiries (Kaafi Hire)", icon: "Users", body: "For employers seeking recruitment, staffing, or workforce administration support." },
  { title: "Legal and consulting enquiries", icon: "Scale", body: "For clients requiring legal advisory, contract work, compliance support, or wider business consulting." },
];

// Integrated advantage columns for the homepage callout
export const INTEGRATED_ADVANTAGE = [
  { title: "One Group, Seven Disciplines", body: "Legal, consulting, engineering, investment, mining, Oil & Gas, and workforce expertise available through a single professional platform." },
  { title: "Consistent Standards", body: "Shared quality, confidentiality, and delivery standards applied across every subsidiary and every engagement." },
  { title: "One Point of Contact", body: "A single relationship co-ordinating the specialists you need — instead of managing several disconnected providers." },
];

export const TESTIMONIALS = [
  { company: "Turkish Airlines", logo_slug: "turkish-airlines", quote: "Salihi Group demonstrated a high level of professionalism, responsiveness, and commercial understanding. Their team provided practical support with strong attention to detail and a clear appreciation of the local operating environment." },
  { company: "Buffair Services", logo_slug: "buffair-services", quote: "Our experience with Salihi Group has been defined by professionalism, reliability, and timely support. Their team understands the importance of accuracy, confidentiality, and operational efficiency." },
  { company: "ENEZ", logo_slug: "enez", quote: "Salihi Group provided responsive and commercially focused support tailored to our operational needs. Their local knowledge and practical approach made them a dependable business partner." },
  { company: "Ziraat Bankası", logo_slug: "ziraat-bankasi", quote: "The Salihi Group team handled our requirements with professionalism, efficiency, and careful attention to detail. Their advice was clear, practical, and aligned with our business objectives." },
  { company: "Docol", logo_slug: "docol", quote: "Salihi Group combines strong local insight with a professional and internationally minded approach. Their commitment to confidentiality, accuracy, and responsive service has been highly valued." },
  { company: "Turkish Maarif Foundation Schools", logo_slug: "turkish-maarif", quote: "We appreciated Salihi Group's solution-oriented approach, clear communication, and timely delivery. Their team remained professional and dependable throughout the engagement." },
  { company: "African Skies Limited", logo_slug: "african-skies", quote: "Salihi Group provided thoughtful and reliable support with a strong understanding of institutional requirements. Their professionalism, responsiveness, and commitment to quality were evident throughout." },
  { company: "ANKA", logo_slug: "anka", quote: "Salihi Group demonstrated strong awareness of the aviation and commercial environment in Somalia. Their team provided clear guidance, timely assistance, and dependable follow-up." },
  { company: "Golden Horn Security", logo_slug: "golden-horn-security", quote: "Our engagement with Salihi Group was handled with professionalism, discretion, and commercial awareness. Their team provided practical support while maintaining high standards of service." },
];

export const TESTIMONIALS_INTRO = "Salihi Group has supported respected local and international organisations across aviation, banking, education, professional services, construction, security, travel, and other key sectors. The testimonials below have been provided with client permission and reflect the professionalism, responsiveness, and integrity that define every engagement across our Group.";

export const CLIENTS = [
  { slug: "african-skies", name: "African Skies Limited" },
  { slug: "alsf", name: "ALSF" },
  { slug: "anka", name: "ANKA" },
  { slug: "buffair-services", name: "Buffair Services" },
  { slug: "cma-cgm", name: "CMA CGM" },
  { slug: "docol", name: "Docol" },
  { slug: "enez", name: "ENEZ" },
  { slug: "favori-airports", name: "Favori Airports" },
  { slug: "finn-church-aid", name: "Finn Church Aid (FCA)" },
  { slug: "global-sovereign-advisory", name: "Global Sovereign Advisory" },
  { slug: "golden-horn-security", name: "Golden Horn Security" },
  { slug: "ifly-air-wings-of-africa", name: "i-Fly Air, Wings of Africa" },
  { slug: "kalexius", name: "Kalexius" },
  { slug: "kepler-karst", name: "Kepler-Karst" },
  { slug: "kpmg", name: "KPMG" },
  { slug: "turkish-airlines", name: "Turkish Airlines" },
  { slug: "turkish-maarif", name: "Turkish Maarif Foundation Schools" },
  { slug: "tpao", name: "Türkiye Petrolleri Anonim Ortaklığı" },
  { slug: "vinje-international", name: "Vinje International" },
  { slug: "ziraat-bankasi", name: "Ziraat Bankası" },
  { slug: "ziraat-katilim", name: "Ziraat Katılım" },
];
